import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { MY_IABLUE_ASSISTANT_ID } from '../lib/constants';
import type {
  UpdateAssistantDto,
  AssistantResponseDto,
  RegenerateWebhookResponseDto,
  AIAssistantStatus,
} from '@/types';

const QUERY_KEY = ['assistants', MY_IABLUE_ASSISTANT_ID];

/**
 * Hook to fetch the My IABlue assistant configuration
 * This is a singleton assistant with a fixed ID
 */
export function useMyIABlueAssistant() {
  return useQuery<AssistantResponseDto>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const { data } = await api.get<AssistantResponseDto>(
        `/assistants/${MY_IABLUE_ASSISTANT_ID}`
      );
      return data;
    },
  });
}

/**
 * Hook to update the My IABlue assistant configuration
 */
export function useUpdateMyIABlueAssistant() {
  const queryClient = useQueryClient();

  return useMutation<AssistantResponseDto, Error, UpdateAssistantDto>({
    mutationFn: async (dto) => {
      const { data } = await api.patch<AssistantResponseDto>(
        `/assistants/${MY_IABLUE_ASSISTANT_ID}`,
        dto
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * Hook to regenerate the webhook URL for the My IABlue assistant
 */
export function useRegenerateMyIABlueWebhook() {
  const queryClient = useQueryClient();

  return useMutation<RegenerateWebhookResponseDto, Error>({
    mutationFn: async () => {
      const { data } = await api.post<RegenerateWebhookResponseDto>(
        `/assistants/${MY_IABLUE_ASSISTANT_ID}/regenerate-webhook`
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * Hook to update only the status of the My IABlue assistant
 * This is optimized for quick status changes without modifying other configurations
 */
export function useUpdateMyIABlueAssistantStatus() {
  const queryClient = useQueryClient();

  return useMutation<AssistantResponseDto, Error, AIAssistantStatus>({
    mutationFn: async (status) => {
      const { data } = await api.patch<AssistantResponseDto>(
        `/assistants/${MY_IABLUE_ASSISTANT_ID}`,
        { status }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
